import React from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import {
    Button,
    Checkbox,
    FormControlLabel,
    CircularProgress,
} from '@material-ui/core';
import apiURL from '../apiURL';

type BoxOption = {
    id: string;
    slug: string;
    name: string;
    price: number;
    active: boolean;
};

const formatPrice = (price: number) =>
    `${price.toFixed(2).replace('.', ',')} €`;

const BoxSupplementCreator = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();

    const [options, setOptions] = React.useState<BoxOption[]>([]);
    const [selected, setSelected] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        if (!record || record.status !== 'paid') return;

        let cancelled = false;
        setLoading(true);

        fetch(`${apiURL}/boxoption`)
            .then(async response => {
                if (!response.ok) throw new Error('Chargement impossible');
                return response.json();
            })
            .then(data => {
                if (cancelled) return;

                const list = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.data)
                    ? data.data
                    : [];

                setOptions(list.filter(
                    (option: BoxOption) =>
                        option.active && option.slug && option.price >= 0
                ));
            })
            .catch(() => {
                if (!cancelled)
                    setError('Impossible de charger les prestations.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [record?.id, record?.status]);

    if (!record || record.status !== 'paid') return null;

    const chosen = options.filter(option =>
        selected.includes(option.slug)
    );

    const total = chosen.reduce(
        (sum, option) => sum + Number(option.price),
        0
    );

    const toggle = (slug: string) => {
        setSelected(previous =>
            previous.includes(slug)
                ? previous.filter(item => item !== slug)
                : [...previous, slug]
        );
    };

    const create = async () => {
        if (!selected.length || saving) return;

        setSaving(true);
        setError('');

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/boxreservation/${record.id}/supplements`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ option_slugs: selected }),
                }
            );

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                const detail = data?.message || data?.errorCode ||
                    data?.error || `Erreur HTTP ${response.status}`;
                throw new Error(
                    Array.isArray(detail) ? detail.join(', ') : String(detail)
                );
            }

            setSelected([]);
            notify('Complément créé en brouillon.', 'success');
            refresh();
        } catch (error) {
            setError(
                error instanceof Error
                    ? error.message
                    : 'Une erreur est survenue.'
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            width: '100%',
            maxWidth: 650,
            padding: 16,
            marginTop: 14,
            border: '1px solid #ddd',
            borderRadius: 6,
        }}>
            <div style={{ fontWeight: 700, marginBottom: 12 }}>
                Ajouter des prestations après réservation
            </div>

            {loading && <CircularProgress size={22} />}

            {options.map(option => (
                <FormControlLabel
                    key={option.slug}
                    control={
                        <Checkbox
                            checked={selected.includes(option.slug)}
                            onChange={() => toggle(option.slug)}
                            color="primary"
                        />
                    }
                    label={`${option.name} — ${formatPrice(Number(option.price))}`}
                    style={{ display: 'block' }}
                />
            ))}

            <div style={{ marginTop: 14, fontWeight: 700 }}>
                Total complément : {formatPrice(total)} TTC
            </div>

            {error && (
                <div style={{ color: '#c62828', marginTop: 10 }}>
                    {error}
                </div>
            )}

            <Button
                variant="contained"
                color="primary"
                onClick={create}
                disabled={!selected.length || saving || loading}
                style={{ marginTop: 14 }}
            >
                {saving
                    ? 'Création...'
                    : 'Créer le complément en brouillon'}
            </Button>
        </div>
    );
};

export default BoxSupplementCreator;
